import type { CategoryModelFragment } from '@generated/graphql';
import { AssociationType } from '@libs/associations';
import type {
  AssociationIbhProductFragmentEnhanced,
  AssociationProductFragmentEnhanced,
  MinimalProduct,
  ProductFragmentEnhanced,
  ProductToProductAssociation,
} from '@models/enhanced-fragments';
import { GraphQlPriceType } from '@models/graphql';
import type { ParsedUrlQuery } from 'querystring';
import type { ProductAttribute, ProductAttributeValueAmountUnit, ProductFragmentAttributes } from './attribute.helper';
import { getAttributeValue, getAttributeValueAsString } from './attribute.helper';
import {
  CATEGORIES_FOR_UNIT_SYSTEM_METRIC,
  CATEGORY_ACCESSORIES_E_CHAINS_KEY,
  IMPERIAL_UNIT_SYSTEM_LOCALES,
  LOCALES_TO_USE_US_PRODUCT_ID,
} from './constants';
import { getPriceDisplayTypeForOrderType, PriceQuantityUnit } from './product-price-utils';
import {
  convertProductAttributeValueAmountUnit,
  MIN_MAX_LENGTH_PER_PRICE_TYPE_CONFIG,
  type MinMaxLengthConfig,
} from '@libs/units';
import type { UnitCode } from '@icommerce/units';
import { getUnitLabel, LengthCode, UnitSystem, WeightCode } from '@icommerce/units';
import { CATEGORY_BEARINGS_IBH, CategoryHelper } from './category.helper';
import { getFeatureFlagValue, getRemoteConfig } from '@libs/abby/abby';
import {
  CATEGORY_3D_MOVEMENT,
  CATEGORY_E_CHAIN,
  CATEGORY_E_CHAIN_INTERIOR_SEPARATORS,
  CATEGORY_E_CHAIN_MOUNTING_BRACKETS,
  CATEGORY_E_CHAIN_STRAIN_RELIEFS,
} from '@icommerce/categories';
import type { BreadcrumbItem } from '@common/Breadcrumb/Breadcrumb';

export type CategoryWithParent = {
  code: string;
  category?: CategoryWithParent;
};

export const flattenCategoryTree = (category: CategoryWithParent): string[] => {
  if (category.category) {
    // if it has a parent flatten its parent
    return [...flattenCategoryTree(category.category), category.code];
  }
  // if it has no parent return its code
  return [category.code];
};

/**
 * returns the category keys as a flat array for a single product
 */
export const getCategoryKeysForProduct = (model: CategoryModelFragment) =>
  model.model_categories.flatMap(category => flattenCategoryTree(category.category as CategoryWithParent));

/**
 * Checks if the given model is enabled/disabled by feature flags. First checks feature flags on category level.
 * For some categories an additional check on the specific model is performed.
 * @param model The model to check
 */
export const isEnabledByFeatureFlag = (
  model: CategoryModelFragment,
  includeIbh = false,
  modelCategories?: string[]
) => {
  const categoryKeys = modelCategories || getCategoryKeysForProduct(model);

  // For e-chain we might need to check if the specific model is enabled
  if (
    categoryKeys.includes(CATEGORY_E_CHAIN) &&
    !categoryKeys.includes(CATEGORY_3D_MOVEMENT) &&
    !categoryKeys.includes(CATEGORY_E_CHAIN_STRAIN_RELIEFS) &&
    !categoryKeys.includes(CATEGORY_E_CHAIN_INTERIOR_SEPARATORS) &&
    !categoryKeys.includes(CATEGORY_E_CHAIN_MOUNTING_BRACKETS) &&
    getFeatureFlagValue('showEChainSelectedModelsOnly')
  ) {
    const envSelectedModelIds = getRemoteConfig<'echainSelectedModelIds', 'String'>('echainSelectedModelIds');
    return envSelectedModelIds.split(',').includes(model.id);
  }
  if (includeIbh && categoryKeys.some(key => CATEGORY_BEARINGS_IBH.includes(key))) {
    return true;
  }
  return CategoryHelper.isEnabled(categoryKeys);
};

/**
 * if the given product has a phaseOutRelation (Association of type "new_product")
 * it will return the productId and modelId of the new product
 */
export const getNewProductAndModelForPhaseOutProducts = (product: ProductFragmentEnhanced) => {
  const newProduct = product.product_to_product_associations?.find(
    association => association.type === AssociationType.NEW_PRODUCT
  )?.productByProductIdTarget;
  if (!newProduct) {
    return null;
  }
  return { productId: newProduct.id, modelId: newProduct.model_id };
};

export const getAssociatedModelIdsFromProduct = (
  product: ProductFragmentEnhanced,
  associationType: AssociationType.ALTERNATIVE_PRODUCT | AssociationType.SUITABLE_PRODUCT | AssociationType.SHELVES
) =>
  product.product_to_model_associations
    ?.filter(association => association.type === associationType)
    ?.map(association => association.model.id);

/**
 * returns the associated Products: suitable Products or Alternative Products if set for given product
 */
export const getAssociationProductReferences = (
  product: ProductFragmentEnhanced,
  associationType: (
    | AssociationType.ALTERNATIVE_PRODUCT
    | AssociationType.SUITABLE_PRODUCT
    | AssociationType.SEPARATORS
    | AssociationType.SHELVES
    | AssociationType.MOUNTING_BRACKETS_TRIFLEX
    | AssociationType.ACCESSORIES
  )[],
  filter: (associationFilter: ProductToProductAssociation) => boolean = () => true
) => {
  const associatedProductReferences = product.product_to_product_associations
    ?.filter(association => associationType.some(type => association.type === type) && filter(association))
    ?.map(associatedProduct => associatedProduct.productByProductIdTarget)
    ?.filter(productByProductIdTarget => Boolean(productByProductIdTarget));
  if (!associatedProductReferences) {
    return null;
  }
  return associatedProductReferences;
};

export const removeUnnecessaryAttributes = (
  product: ProductFragmentEnhanced | AssociationProductFragmentEnhanced | AssociationIbhProductFragmentEnhanced,
  requiredAttributeNames: string[]
): ProductFragmentEnhanced | AssociationProductFragmentEnhanced | AssociationIbhProductFragmentEnhanced => {
  if (!product.attributes) {
    return product;
  }

  const availableAttributeNames = Object.keys(product.attributes);
  availableAttributeNames.forEach(attributeName => {
    if (!requiredAttributeNames.includes(attributeName)) {
      // eslint-disable-next-line no-param-reassign
      delete product.attributes?.[attributeName];
    }
  });

  return product;
};

// TODO: remove this function when the deprecated priceType is removed from the DB
export const removeDeprecatedAssociationProductPriceType = (
  associationProduct: AssociationProductFragmentEnhanced
): AssociationProductFragmentEnhanced => {
  if (!associationProduct.aggregated_product_prices) {
    return associationProduct;
  }

  associationProduct.aggregated_product_prices.forEach(aggregatedProductPrice => {
    if ((aggregatedProductPrice.prices as Record<string, unknown>).priceType) {
      // eslint-disable-next-line no-param-reassign
      delete (aggregatedProductPrice.prices as Record<string, unknown>).priceType;
    }
  });

  return associationProduct;
};

export const getProductTitle = (product: ProductFragmentEnhanced) =>
  getAttributeValueAsString(product.attributes?.series_description as ProductAttribute<string>);

export const getProductDescription = (product: ProductFragmentEnhanced) =>
  getAttributeValue<string>(product.attributes?.attr_description as ProductAttribute<string>) || '';

export const getProductUsp = (product: ProductFragmentEnhanced) =>
  getAttributeValueAsString(product.attributes?.attr_USP as ProductAttribute<string>);

export const getProductMetaDescription = (product: ProductFragmentEnhanced) =>
  getAttributeValueAsString(product.attributes?.series_description as ProductAttribute<string>);

export const getProductCmsId = (product: ProductFragmentEnhanced) =>
  getAttributeValue<number>(product.attributes?.product_ID_CMS as ProductAttribute<number>);

export const getProductPriceType = (product: ProductFragmentEnhanced) =>
  getPriceDisplayTypeForOrderType(
    getAttributeValue<string>(product.attributes?.order_type as ProductAttribute<string>)
  );

/**
 * @param query
 * @returns the value of the artnr query param while disregarding case sensitivity
 */
export const getQueryArtNr = (query: ParsedUrlQuery) => {
  // case-insensitive artnr
  const artnrRegex = /artnr/gi;

  const artnrQueryEntry = Object.entries(query).find(([key]) => artnrRegex.test(key));

  // should be a key/value tuple returned by object.entries ([key])
  return artnrQueryEntry?.length !== 2 ? (query.artnr as string) : (artnrQueryEntry[1] as string);
};

/**
 * Finds the product with the lowest price from an array of products.
 * If any product has an invalid price (because the lowest_price field is is only returned when using catalog-data-api)
 * it defaults back to the original behavior with products[0]
 * @param {MinimalProduct[]} products - Released and sellable products
 * @returns {MinimalProduct}
 */
export const findProductWithLowestPrice = (products: MinimalProduct[]): MinimalProduct => {
  const hasInvalidPrice = products.some(p => p.lowest_price === undefined || p.lowest_price === null);

  if (hasInvalidPrice) {
    return products[0];
  }

  const minIndex = products.reduce((indexOfCheapest, currentProduct, currentIndex) => {
    const cheapestPrice = products[indexOfCheapest].lowest_price;
    return currentProduct.lowest_price < cheapestPrice ? currentIndex : indexOfCheapest;
  }, 0);

  return products[minIndex];
};

/**
 * Find the international sku for a given (possibly us-specific) product sku in a list of products.
 * @param products List of products to search sku
 * @param sku International or us-specific sku to find
 * @returns The international sku of the found or first product (if none has been found)
 */
export const findInternationalProductSku = (products: MinimalProduct[], sku: string | null) => {
  if (!products || products.length === 0) {
    throw Error('Unable to search sku in invalid/empty product list');
  }

  if (!sku || sku === '') {
    return findProductWithLowestPrice(products).id;
  }

  const foundVariant = products.find(product => product.id === sku || product.id_us === sku);
  const product = foundVariant || products[0];
  return foundVariant ? foundVariant.id : product.id;
};

export type LengthConfig = { min: number; max: number; default: number; onlyInt: boolean };

/**
 *
 * @param isTriflex This is needed to be able to distinguish between triflex
 * and the echains that are rendered on EchainProductPage
 */
export const getLengthUnitCodeByPriceType = (
  priceType: GraphQlPriceType,
  unitSystem: UnitSystem,
  isTriflex = false
): LengthCode | undefined => {
  switch (priceType) {
    case GraphQlPriceType.ChainLength:
      if (unitSystem === UnitSystem.metric) {
        if (isTriflex) {
          return LengthCode.MILLIMETER;
        }
        return LengthCode.METER;
      }
      return LengthCode.FEET;
    case GraphQlPriceType.Length:
    case GraphQlPriceType.LengthWithoutCopper:
    case GraphQlPriceType.LengthAndPiece:
      return LengthCode.METER;
    case GraphQlPriceType.ChainLink:
      return unitSystem === UnitSystem.metric ? LengthCode.MILLIMETER : LengthCode.FEET;
    case GraphQlPriceType.LengthWithCuttingCostWithoutCopper:
    case GraphQlPriceType.LengthAndPieceWithoutCopper:
    case GraphQlPriceType.LeadScrewLength:
    case GraphQlPriceType.TribotapeLength:
      return LengthCode.MILLIMETER;
    default:
      // The following GraphQlPriceTypes do not need a length definition: PieceWithCopper & Piece & PieceWithChainLengthDependency
      return undefined;
  }
};

/**
 * @param priceType affects the min and max length and used to find out the unit
 * @param productAttributes list of attributes - necessary to read product data
 * @param defaultLength default value for length according to the buyBoxConfiguration
 * @param lengthUnitCodeForUnitSystem  value of the length unit according to the unit-system
 * @param unitSystem  the unit-system
 * @returns an object containing the min, max and default length values including an onlyInt flag
 */
export const getProductLengthConfig = (
  priceType: GraphQlPriceType,
  productAttributes: ProductFragmentAttributes | null | undefined,
  defaultLength: number,
  lengthUnitCodeForUnitSystem: UnitCode,
  unitSystem: UnitSystem
): LengthConfig => {
  const getConvertedAttributeValue = (attribute: ProductAttribute<ProductAttributeValueAmountUnit>) =>
    attribute && convertProductAttributeValueAmountUnit(attribute, lengthUnitCodeForUnitSystem);

  const getAmountUnitAttribute = (value: number): ProductAttribute<ProductAttributeValueAmountUnit> => {
    let unit: UnitCode = getLengthUnitCodeByPriceType(priceType, unitSystem);
    const orderType = getAttributeValue<string>(productAttributes.order_type as ProductAttribute<string>);
    if (['200', '210', '220', '401'].includes(orderType)) {
      unit = lengthUnitCodeForUnitSystem;
    }
    return {
      amount: value,
      unit,
    };
  };

  const lengthConfigPerPriceType: MinMaxLengthConfig =
    MIN_MAX_LENGTH_PER_PRICE_TYPE_CONFIG[
      priceType as
        | GraphQlPriceType.LengthAndPieceWithoutCopper
        | GraphQlPriceType.LengthWithCuttingCostWithoutCopper
        | GraphQlPriceType.ChainLink
        | GraphQlPriceType.LeadScrewLength
        | GraphQlPriceType.TribotapeLength
    ] || MIN_MAX_LENGTH_PER_PRICE_TYPE_CONFIG.default;

  const minLength = getAttributeValue<ProductAttributeValueAmountUnit>(
    productAttributes?.[lengthConfigPerPriceType?.minAttribute] as ProductAttribute<ProductAttributeValueAmountUnit>
  );

  const maxLength = getAttributeValue<ProductAttributeValueAmountUnit>(
    productAttributes?.[lengthConfigPerPriceType?.maxAttribute] as ProductAttribute<ProductAttributeValueAmountUnit>
  );

  const onlyInt = priceType === GraphQlPriceType.LengthAndPieceWithoutCopper && unitSystem === UnitSystem.metric;

  if (minLength?.amount && maxLength?.amount) {
    // For products with order type Tribotape 1520 we always have to use the defaultLength coming from getBuyBoxConfiguration
    const defaultLengthForPriceType =
      priceType === GraphQlPriceType.TribotapeLength
        ? getConvertedAttributeValue(getAmountUnitAttribute(defaultLength))
        : getConvertedAttributeValue(minLength);
    return {
      min: getConvertedAttributeValue(minLength),
      max: getConvertedAttributeValue(maxLength),
      default: defaultLengthForPriceType,
      onlyInt,
    };
  }
  return {
    min: getConvertedAttributeValue(getAmountUnitAttribute(lengthConfigPerPriceType.minLengthDefault)),
    max: getConvertedAttributeValue(getAmountUnitAttribute(lengthConfigPerPriceType.maxLengthDefault)),
    default: getConvertedAttributeValue(getAmountUnitAttribute(defaultLength)),
    onlyInt,
  };
};

export const getProductIdForLocale = (product: Pick<ProductFragmentEnhanced, 'id' | 'id_us'>, locale: string): string =>
  product.id_us && LOCALES_TO_USE_US_PRODUCT_ID.includes(locale) ? product.id_us : product.id;

export const determineProductUnitSystem = (locale: string, primaryCategoryCode: string): UnitSystem => {
  if (CATEGORIES_FOR_UNIT_SYSTEM_METRIC.includes(primaryCategoryCode)) {
    return UnitSystem.metric;
  }

  return IMPERIAL_UNIT_SYSTEM_LOCALES.includes(locale) ? UnitSystem.imperial : UnitSystem.metric;
};

export const getPriceQuantityUnitLabel = (priceQuantityUnit: PriceQuantityUnit): string => {
  switch (priceQuantityUnit) {
    case PriceQuantityUnit.LINK:
      return 'PRODUCT_OVERVIEW.PRICE_BOX.CHAINLINK.UNIT_SINGULAR';
    case PriceQuantityUnit.PIECE:
      return 'PRODUCT_OVERVIEW.PRICE_BOX.LENGTH_AND_PIECE.PIECE_ABBREVIATION';
    case PriceQuantityUnit.METER:
      return getUnitLabel(LengthCode.METER);
    case PriceQuantityUnit.FEET:
      return getUnitLabel(LengthCode.FEET);
    case PriceQuantityUnit.MILLIMETER:
      return getUnitLabel(LengthCode.MILLIMETER);
    case PriceQuantityUnit.KILOGRAM:
      return getUnitLabel(WeightCode.KILOGRAM);
    case PriceQuantityUnit.INCH:
      return getUnitLabel(LengthCode.INCH);
    default:
      return priceQuantityUnit;
  }
};

/**
 *
 * @deprecated use getPriceQuantityUnitLabel instead,
 * in case the priceQuantityUnit from the database is available.
 * The priceQuantityUnit is currently missing in the database table "aggregated_product_prices"
 * and only available in the "prices" table.
 */
export const getPriceQuantityUnitLabelDeprecated = (
  locale: string,
  priceType: GraphQlPriceType,
  primarilyProductCategory: string
): string => {
  const unitSystem = determineProductUnitSystem(locale, primarilyProductCategory);

  // rewrite the priceTypeToUnitDefaultMapping const in a switch case
  switch (priceType) {
    case GraphQlPriceType.ChainLink:
      return 'PRODUCT_OVERVIEW.PRICE_BOX.CHAINLINK.UNIT_SINGULAR';
    case GraphQlPriceType.ChainLength:
    case GraphQlPriceType.Length:
    case GraphQlPriceType.LeadScrewLength:
    case GraphQlPriceType.LengthWithoutCopper:
    case GraphQlPriceType.LengthWithCuttingCostWithoutCopper:
    case GraphQlPriceType.TribotapeLength:
      return unitSystem === UnitSystem.metric ? PriceQuantityUnit.METER : PriceQuantityUnit.FEET;
    case GraphQlPriceType.LengthAndPiece:
    case GraphQlPriceType.Piece:
    case GraphQlPriceType.PieceWithCopper:
    case GraphQlPriceType.PieceWithChainLengthDependency:
      return 'PRODUCT_OVERVIEW.PRICE_BOX.LENGTH_AND_PIECE.PIECE_ABBREVIATION';
    case GraphQlPriceType.LengthAndPieceWithoutCopper:
      return PriceQuantityUnit.MILLIMETER;
    default:
      return priceType;
  }
};

export const isEchainAccessory = (breadcrumbItem: BreadcrumbItem[]) => {
  const hasAccessoriesKey = breadcrumbItem.some(item => item.key === CATEGORY_ACCESSORIES_E_CHAINS_KEY);
  const isInParentKeys = breadcrumbItem.some(item => item.parentKeys?.includes(CATEGORY_ACCESSORIES_E_CHAINS_KEY));
  return hasAccessoriesKey && isInParentKeys;
};
